// Seeded PRNG: sfc32 with state serialized as 4 uint32s inside GameState.
// All distribution helpers draw from the single stream; none cache state
// outside the array (a cached Box–Muller spare would break replay).

export type RngState = [number, number, number, number];

function splitmix32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x9e3779b9) | 0;
    let t = a ^ (a >>> 16);
    t = Math.imul(t, 0x21f0aaad);
    t = t ^ (t >>> 15);
    t = Math.imul(t, 0x735a2d97);
    return (t ^ (t >>> 15)) >>> 0;
  };
}

export function seedRng(seed: number): RngState {
  const sm = splitmix32(seed);
  const s: RngState = [sm(), sm(), sm(), sm()];
  // warm up: decorrelate from seed structure
  for (let i = 0; i < 12; i++) sfc32Raw(s);
  return s;
}

/** Advance state in place; return uint32. */
function sfc32Raw(s: RngState): number {
  let a = s[0] >>> 0;
  let b = s[1] >>> 0;
  let c = s[2] >>> 0;
  let d = s[3] >>> 0;
  const t = (a + b) | 0;
  a = b ^ (b >>> 9);
  b = (c + (c << 3)) | 0;
  c = ((c << 21) | (c >>> 11)) | 0;
  d = (d + 1) | 0;
  const out = (t + d) | 0;
  c = (c + out) | 0;
  s[0] = a >>> 0;
  s[1] = b >>> 0;
  s[2] = c >>> 0;
  s[3] = d >>> 0;
  return out >>> 0;
}

/** Mutating wrapper bound to a state array (the draft state's `rng`). */
export interface Rng {
  uniform(): number; // [0, 1)
  int(n: number): number; // 0..n-1
  normal(mu: number, sigma: number): number;
  logNormal(mu: number, sigma: number): number;
  poisson(lambda: number): number;
  bernoulli(p: number): boolean;
}

export function makeRng(state: RngState): Rng {
  const uniform = () => sfc32Raw(state) / 4294967296;
  const normal = (mu: number, sigma: number): number => {
    // Box–Muller, both uniforms consumed every call, no spare cached.
    const u1 = Math.max(uniform(), 1e-12);
    const u2 = uniform();
    return mu + sigma * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  };
  const poisson = (lambda: number): number => {
    if (!(lambda > 0)) return 0;
    if (lambda >= 30) {
      return Math.max(0, Math.round(normal(lambda, Math.sqrt(lambda))));
    }
    // Knuth product method (variable draw count is fine: state lives in GameState)
    const limit = Math.exp(-lambda);
    let k = 0;
    let p = 1;
    do {
      k += 1;
      p *= uniform();
    } while (p > limit);
    return k - 1;
  };
  return {
    uniform,
    int: (n: number) => Math.floor(uniform() * n),
    normal,
    logNormal: (mu: number, sigma: number) => Math.exp(normal(mu, sigma)),
    poisson,
    bernoulli: (p: number) => uniform() < p,
  };
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}
